import { Protect } from '@clerk/clerk-react'
import { useStatsigClient } from '@statsig/react-bindings'
import { IconChevronDown } from '@tabler/icons-react'
import * as React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { clearShowAppAlertFlag, hasPendingUnsavedNavigation } from '@/utility/showAppAlertStorage'
import { RELEASE_GATE_V2_13_0 } from '../../utility/releaseStatsigGateConstants.js'
import AlertBox from '../AlertBox/AlertBox'
import ToolTip from '../ToolTip/ToolTip'

const _alertInstance = 0
const MenuExpand = ({
  icon,
  link,
  menu,
  submenu,
  headerExpand,
  setHeaderExpand,
  type,
  selected,
  setSelected,
  setSubmenu,
  subMenuName,
  setSubMenuName,
  theme,
  permissions,
  accessPermissions,
  flags,
  width,
  showTooltip,
  selectedTextClass,
  onDiscardUnsavedChanges,
  ..._props
}) => {
  const [expandMenu, setExpandMenu] = React.useState(true)
  const navigate = useNavigate()
  const { client } = useStatsigClient()
  const [showAppAlert, setShowAppAlert] = React.useState({
    show: false,
    path: '',
  })
  const location = useLocation()

  React.useEffect(() => {
    if (type === selected && submenu?.length) {
      setExpandMenu(true)
      setSubmenu(true)
    } else {
      setExpandMenu(false)
      setSubmenu(false)
    }
    if (!headerExpand) {
      setExpandMenu(false)
      setSubmenu(false)
    }
  }, [headerExpand])

  React.useEffect(() => {
    if (selected !== type) {
      setExpandMenu(false)
    }
  }, [selected])

  const isSelected = selected === type
  const hasSubmenu = Array.isArray(submenu) && submenu.length > 0
  const selectedClass =
    selectedTextClass || (hasSubmenu ? 'text-Text-medium-06' : 'text-Misc-brand')
  const menuTextClass = isSelected ? selectedClass : 'text-Text-small-04'
  const submenuChevronClass = isSelected
    ? selectedTextClass || 'text-Text-large-08'
    : 'text-Icon-strong-06'

  const uagEnhancements = client.checkGate(RELEASE_GATE_V2_13_0)

  return (
    <>
      {showAppAlert.show && (
        <AlertBox
          title="You have unsaved changes."
          message="Are you sure you want to leave this page? Your changes will be lost."
          confirmBtnText="Confirm"
          confirmBtnType="primary"
          cancelBtnType="secondary-v3"
          onClose={() => {
            setShowAppAlert(false)
          }}
          onConfirm={() => {
            if (onDiscardUnsavedChanges && typeof onDiscardUnsavedChanges === 'function') {
              onDiscardUnsavedChanges()
            }
            setShowAppAlert((prev) => ({ ...prev, show: false, path: '' }))
            clearShowAppAlertFlag()
            if (showAppAlert.path === location.pathname) {
              window.location.reload()
            } else {
              navigate({
                pathname: showAppAlert.path,
                search: '',
                replace: true,
              })
            }
          }}
          data-testid="header-menu-expand-system-navigate-alert"
        />
      )}
      <Protect
        condition={(has) => {
          if (uagEnhancements && accessPermissions !== undefined) {
            return accessPermissions
          } else if (permissions) {
            return (
              permissions.some((permission) => has({ permission })) &&
              (flags ? flags.some((flag) => client.checkGate(flag)) : true)
            )
          } else {
            return true
          }
        }}
      >
        <div
          data-testid={`menu-expand-${type}`}
          className={`flex flex-col justify-start ${width && !headerExpand ? width : 'w-full'}`}
        >
          <div
            data-testid={`menu-item-${type}`}
            className={`w-full py-2 rounded-md flex items-center gap-2 px-1 cursor-pointer ${
              !expandMenu ? 'group' : ''
            } ${
              selected === type && !expandMenu
                ? theme === 'dark'
                  ? 'bg-Button-Default-alternative hover:bg-Button-Default-alternative'
                  : 'bg-BG-Selected-brand-00 hover:bg-BG-Selected-brand-00'
                : 'hover:bg-BG-Default-neutral-00'
            } ${!headerExpand ? 'justify-center' : ''}`}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()

              if (headerExpand && submenu?.length) {
                setExpandMenu(!expandMenu)
                setSubmenu(!expandMenu)
              } else if (submenu?.length) {
                setHeaderExpand(true)
                setExpandMenu(true)
              }
              if ((submenu === undefined || submenu?.length === 0) && link) {
                if (hasPendingUnsavedNavigation()) {
                  setShowAppAlert((prev) => ({
                    ...prev,
                    show: true,
                    path: link,
                  }))
                  return false
                }
                setShowAppAlert((prev) => ({ ...prev, show: false, path: '' }))
                setSubMenuName('', type)
                setSelected(type)
                setHeaderExpand(false)
                navigate({ pathname: link, search: '' })
              } else if (headerExpand && !submenu?.length) {
                setHeaderExpand(false)
              } else {
                setSelected(type)
              }
            }}
          >
            {showTooltip && !headerExpand ? (
              <ToolTip text={menu} position="right" offset={[0, 16]} truncate={false}>
                <div
                  data-testid={`menu-icon-${type}`}
                  className={`flex items-center rounded justify-center ${
                    isSelected ? selectedClass : ''
                  } ${
                    theme === 'dark'
                      ? 'bg-Button-Default-alternative anim hover:bg-Button-Default-alternative'
                      : ''
                  }`}
                >
                  {icon}
                </div>
              </ToolTip>
            ) : (
              <div
                data-testid={`menu-icon-${type}`}
                className={`flex items-center rounded justify-center ${
                  isSelected ? selectedClass : ''
                } ${
                  theme === 'dark'
                    ? 'bg-Button-Default-alternative hover:bg-Button-Default-alternative'
                    : ''
                }`}
              >
                {icon}
              </div>
            )}
            {headerExpand ? (
              <div data-testid={`menu-text-container-${type}`} className="flex items-center gap-2">
                <span
                  data-testid={`menu-text-${type}`}
                  className={`text-sm font-bold ${menuTextClass}`}
                >
                  {menu}
                </span>
                {submenu?.length ? (
                  <IconChevronDown
                    data-testid={`menu-chevron-${type}`}
                    stroke={2.5}
                    className={`h-4 w-4 ${expandMenu ? 'rotate-180' : ''} ${submenuChevronClass}`}
                  />
                ) : null}
              </div>
            ) : null}
          </div>
          {headerExpand && expandMenu && submenu?.length ? (
            <React.Fragment>
              {submenu.map((sm, index) => {
                return (
                  <Protect
                    key={sm.link ?? `${type}-${String(sm.label)}-${index}`}
                    condition={(has) => {
                      if (uagEnhancements && sm.accessPermissions !== undefined) {
                        return sm.accessPermissions
                      } else if (sm.permissions) {
                        return (
                          sm.permissions.some((permission) => has({ permission })) &&
                          (sm.hasOwnProperty('flag') ? client.checkGate(sm.flag) : true)
                        )
                      } else {
                        return true
                      }
                    }}
                  >
                    <div
                      data-testid={`submenu-item-${type}-${sm.label
                        .replace(/\s+/g, '-')
                        .toLowerCase()}`}
                      className={`w-full py-2 rounded flex items-center gap-2 pl-4 px-1 cursor-pointer group ${
                        subMenuName === sm.label
                          ? 'bg-BG-Selected-brand-00 hover:bg-BG-Selected-brand-00'
                          : 'hover:bg-BG-Default-neutral-00'
                      }`}
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        setHeaderExpand(false)
                        if (sm?.link) {
                          if (
                            sm?.link === '/shipment/live' ||
                            sm?.link === '/shipment/commandcenter'
                          ) {
                            localStorage.viewType = 'map'
                          }
                          if (hasPendingUnsavedNavigation()) {
                            setShowAppAlert((prev) => ({
                              ...prev,
                              show: true,
                              path: sm?.link,
                            }))
                            return false
                          }
                          setShowAppAlert((prev) => ({
                            ...prev,
                            show: false,
                            path: '',
                          }))
                          clearShowAppAlertFlag()
                          setSubMenuName(sm.label, type)
                          navigate({ pathname: sm.link, search: '' })
                        }
                      }}
                    >
                      <div
                        data-testid={`submenu-icon-${type}-${sm.label
                          .replace(/\s+/g, '-')
                          .toLowerCase()}`}
                        className={`flex items-center justify-center ${
                          subMenuName === sm.label ? 'text-Misc-brand' : ''
                        }`}
                      >
                        {sm.icon}
                      </div>
                      <div
                        data-testid={`submenu-text-container-${type}-${sm.label
                          .replace(/\s+/g, '-')
                          .toLowerCase()}`}
                        className="flex items-center gap-2"
                      >
                        <span
                          data-testid={`submenu-text-${type}-${sm.label
                            .replace(/\s+/g, '-')
                            .toLowerCase()}`}
                          className={`text-sm whitespace-pre ${
                            subMenuName === sm.label
                              ? 'text-Misc-brand group-hover:text-Misc-brand'
                              : 'text-Text-small-04'
                          }`}
                        >
                          {sm.label}
                        </span>
                      </div>
                    </div>
                  </Protect>
                )
              })}
            </React.Fragment>
          ) : null}
        </div>
      </Protect>
    </>
  )
}

export default MenuExpand
